/**
 * Copyright © 2026 Adnuntius AS.
 */
import {ChangeDetectorRef, DOCUMENT, Component, Inject, Input, NgZone, OnChanges, OnInit, SimpleChanges} from '@angular/core';
import {TranslateService} from '@ngx-translate/core';
import * as _ from 'lodash';
import * as moment from 'moment';
import writeXlsxFile from 'write-excel-file';
import JSZip from 'jszip';

import template from './creative-tag.component.html';
import {AdTagReplacers} from '../ad-unit/ad-tag-replacers';
import {CreativeDefaults} from './creative-defaults';
import {Uuid} from '../../components/util/uuid';
import {DataViewResource} from '../../components/api/resources/resource/data-view';
import {AdnSelectSearchElement} from '../common/directives/adn-select/adn-select.component';
import {formatNetworkDateTime} from '../common/locale/network-date-picker-utils';
import {AdUnitResource} from '../../components/api/resources/resource/adunit.resource';
import {CreativeResource} from '../../components/api/resources/resource/creative.resource';
import {LocalNetworkProfile} from '../../components/session/local-network-profile';
import {SearchResource} from '../../components/api/resources/resource/search';
import {WINDOW} from '../angular-platform-providers';
import {deferMarkForCheck} from '../common/util/defer-mark-for-check';
import {StateService} from '@uirouter/core';

@Component({
  standalone: false,
  selector: 'adn-creative-tag-component',
  template
})
export class CreativeTagComponent implements OnInit, OnChanges {
  @Input() model: any;
  @Input() type: string;
  @Input() creatives: any[];
  @Input() cacheSelect: boolean | string;
  @Input() downloadOption: boolean | string;

  showDownloads = false;
  showAdvancedOptions = false;
  targetId = 'adn-' + Uuid.generate().substring(0, 6);
  useCookies = true;
  clickTrackingUrl = '';
  targeting = false;
  showOptions = ['STANDARD_JAVASCRIPT', 'GOOGLE_AD_MANAGER', 'GOOGLE_CAMPAIGN_MANAGER', 'DV360', 'ADFORM', 'XANDR'];
  showOption = 'STANDARD_JAVASCRIPT';
  optionCode: Record<string, any> = {};
  dimensions: any[] = [[]];
  adUnit: any = null;
  adUnitResults: any[] = [];
  auW: number;
  auH: number;
  lineItemName: string;
  clickTracker: string;
  impTracker: string;
  emailCode: string;
  networkTagId: string;
  scriptServeCode: string;
  scriptServeCodeGAM: string;
  codesReady = false;

  private parentId: string;
  private creative: any;
  private parentType: string;
  private adTagId = '';
  private actionData: any;
  private loadedCacheId: string;
  private initializedWithoutCache = false;

  searchAdUnits = (searchElement: AdnSelectSearchElement): void => {
    this.searchResource.query({q: searchElement.search, types: 'AdUnit'}).then((data: any) => {
      this.ngZone.run(() => {
        this.adUnitResults = data.searchResults;
      });
    });
  };

  constructor(
    @Inject(LocalNetworkProfile) private readonly LocalNetworkProfile: any,
    @Inject(AdUnitResource) private readonly AdUnit: any,
    @Inject(CreativeResource) private readonly Creative: any,
    @Inject(SearchResource) private readonly searchResource: any,
    private readonly $state: StateService,
    @Inject(DOCUMENT) private readonly document: any,
    @Inject(WINDOW) private readonly window: any,
    private readonly translate: TranslateService,
    private readonly dataViewResource: DataViewResource,
    private readonly ngZone: NgZone,
    private readonly cdr: ChangeDetectorRef
  ) {
  }

  ngOnInit(): void {
    this.type = this.type || 'CREATIVE';
    this.targeting = this.type === 'ORDER' || this.type === 'LINE_ITEM' || this.type === 'CREATIVE_SET';
    this.loadTagData();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.model) {
      this.initializedWithoutCache = false;
      this.loadedCacheId = null;
      this.loadTagData();
    }
  }

  private resolveLineItemId(): string {
    return _.get(this.model, ['id'])
      || _.get(this.model, ['model', 'id'])
      || _.get(this.model, ['lineItem', 'id'])
      || _.get(this.model, ['apiModel', 'id'])
      || '';
  }

  private resolveDirectParentId(data: any): string {
    if (this.type === 'ORDER' || this.type === 'LINE_ITEM' || this.type === 'CREATIVE_SET') {
      return _.get(data, ['id']) || '';
    }
    return '';
  }

  private loadTagData(): void {
    if (this.isTruthy(this.cacheSelect)) {
      const id = this.resolveLineItemId();
      if (!id || this.loadedCacheId === id) {
        return;
      }
      this.loadedCacheId = id;
      this.dataViewResource.getFromCache(id).then((data: any) => {
        this.ngZone.run(() => this.whereTheActionHappens(data));
      });
      return;
    }
    if (this.model && !this.initializedWithoutCache) {
      this.initializedWithoutCache = true;
      this.whereTheActionHappens(this.model);
    }
  }

  get adUnitLabelFallback(): string {
    return _.get(this.model, ['model', 'adUnit', 'name']) || '';
  }

  get emailCodeReason(): string {
    return _.get(this.model, ['emailCodeReason']) || '';
  }

  get showDownloadUi(): boolean {
    return this.showDownloads || !this.isTruthy(this.downloadOption);
  }

  get hasDownloadOption(): boolean {
    return this.isTruthy(this.downloadOption);
  }

  get showZippedHtmlDownload(): boolean {
    return this.type === 'CREATIVE_SET' || this.type === 'LINE_ITEM';
  }

  removeDimensions(index: number): void {
    _.pullAt(this.dimensions, index);
  }

  addDimensions(): void {
    this.dimensions = this.dimensions || [];
    this.dimensions.push([]);
  }

  updateThirdPartyCode(): void {
    this.networkTagId = this.LocalNetworkProfile.get('tagId');
    const whitelabelDomain = AdTagReplacers.getNetworkWhitelabelDomain(this.LocalNetworkProfile.getNetworkId());

    const updateCode = () => {
      // null/undefined → string and false → true flip spinner [resolved]; defer to avoid NG0100.
      deferMarkForCheck(this.cdr, () => {
        this.clickTracker = CreativeDefaults.getClickTracker(
          this.parentType, this.parentId, this.creative, this.adTagId,
          this.LocalNetworkProfile.get('tagId'), this.useCookies
        );
        this.impTracker = CreativeDefaults.getImpTracker(
          this.parentType, this.parentId, this.creative, this.adTagId,
          this.LocalNetworkProfile.get('tagId'), this.useCookies
        );
        this.optionCode.STANDARD_JAVASCRIPT.code = this.generateCode('STANDARD_JAVASCRIPT');
        this.optionCode.STANDARD_JAVASCRIPT.code2 = this.generateCode('STANDARD_JAVASCRIPT', 2);
        this.optionCode.STANDARD_JAVASCRIPT.code2Title = 'Direct Script version of Default Adnuntius Tag';
        this.optionCode.STANDARD_JAVASCRIPT.code3 = this.generateCode('STANDARD_JAVASCRIPT', 3);
        this.optionCode.STANDARD_JAVASCRIPT.code3Title = 'Iframe version of Default Adnuntius Tag';
        this.optionCode.GOOGLE_AD_MANAGER.code = this.generateCode('GOOGLE_AD_MANAGER');
        this.optionCode.GOOGLE_AD_MANAGER.code2 = this.generateCode('GOOGLE_AD_MANAGER', 2);
        this.optionCode.GOOGLE_AD_MANAGER.code2Title = 'Direct Script Version of Tag for Google Ad Manager';
        this.optionCode.GOOGLE_AD_MANAGER.limited = true;
        this.optionCode.DV360.code = this.generateCode('DV360');
        this.optionCode.DV360.limited = true;
        this.optionCode.XANDR.code = this.generateCode('XANDR');
        this.optionCode.ADFORM.code = this.generateCode('ADFORM');
        this.optionCode.ADFORM.xls = true;
        this.optionCode.GOOGLE_CAMPAIGN_MANAGER.code = this.generateCode('GOOGLE_CAMPAIGN_MANAGER');
        this.optionCode.GOOGLE_CAMPAIGN_MANAGER.limited = true;
        this.optionCode.GOOGLE_CAMPAIGN_MANAGER.xls = true;
        const destUrl = _.find(_.get(this.creative, ['cToUrls'], []), (v) => !!v) || '';
        this.emailCode = CreativeDefaults.getEmail(
          this.parentType, this.parentId, this.creative, this.adTagId,
          this.LocalNetworkProfile.get('tagId'), destUrl, this.useCookies
        );
        if (whitelabelDomain) {
          this.clickTracker = AdTagReplacers.replace(this.clickTracker, whitelabelDomain, this.networkTagId);
          this.impTracker = AdTagReplacers.replace(this.impTracker, whitelabelDomain, this.networkTagId);
          this.scriptServeCode = AdTagReplacers.replace(this.scriptServeCode, whitelabelDomain, this.networkTagId);
          this.scriptServeCodeGAM = AdTagReplacers.replace(this.scriptServeCodeGAM, whitelabelDomain, this.networkTagId);
          this.emailCode = AdTagReplacers.replace(this.emailCode, whitelabelDomain, this.networkTagId);
        }
        this.codesReady = true;
      });
    };

    if (this.adUnit && this.adUnit.id) {
      this.AdUnit.get({id: this.adUnit.id}, (au: any) => {
        this.adTagId = _.get(au, 'tagId');
        updateCode();
      });
    } else {
      this.adTagId = '';
      updateCode();
    }
  }

  downloadAs(code: string, title: string): void {
    const blob = new this.window.Blob([code], {type: 'text/plain'});
    const a = this.document.createElement('a');
    a.href = this.window.URL.createObjectURL(blob);
    a.download = _.lowerCase(title).replace(/ /g, '-') + '.txt';
    this.document.body.appendChild(a);
    a.click();
    this.document.body.removeChild(a);
    this.window.URL.revokeObjectURL(a.href);
  }

  downloadAsShowOption(): void {
    const key = 'tag.' + this.showOption + '.' + this.showOption;
    const title = this.translate.instant(key);
    this.downloadAs(this.optionCode[this.showOption].code, title);
  }

  downloadAsCalculated(downloadFormat: string, codeFormat?: string): void {
    this.targeting = false;
    this.resolveDownloadCreatives().then((creatives) => {
      this.startCalculatedDownload(downloadFormat, codeFormat, creatives);
    });
  }

  private isTruthy(value: boolean | string): boolean {
    return value === true || value === 'true';
  }

  private parentDownloadName(): string {
    return this.lineItemName
      || _.get(this.actionData, ['lineItem', 'name'])
      || _.get(this.actionData, ['name'])
      || 'download';
  }

  private activeCreatives(creatives: any[]): any[] {
    return _.filter(creatives || [], (c) => !c.objectState || c.objectState === 'ACTIVE');
  }

  private resolveDownloadCreatives(): Promise<any[]> {
    if (this.type === 'CREATIVE_SET' && this.parentId) {
      return this.Creative.query({creativeSet: this.parentId, concise: true, pageSize: 1000}).$promise.then((page: any) => {
        const queried = this.activeCreatives(page.results || []);
        return queried.length > 0 ? queried : this.fallbackCreatives();
      });
    }
    return Promise.resolve(this.fallbackCreatives());
  }

  private fallbackCreatives(): any[] {
    const fromInput = this.activeCreatives(this.creatives);
    if (fromInput.length > 0) {
      return fromInput;
    }
    return this.activeCreatives(_.get(this.actionData, 'creatives'));
  }

  private startCalculatedDownload(downloadFormat: string, codeFormat: string, creatives: any[]): void {
    const data = this.actionData;
    const a = this.document.createElement('a');
    let promise: any;
    const fileBase = _.kebabCase(this.parentDownloadName());

    let adTagCount = 1;
    if (downloadFormat === 'ZIPPED_HTML') {
      const zip = new JSZip();
      const networkId = this.LocalNetworkProfile.get('tagId');
      _.forEach(creatives, (c) => {
        const targetId = Uuid.generate().replace(/-/g, '').substring(0, 6);
        zip.file(_.kebabCase(c.name) + '.html', CreativeDefaults.getZippedHtml(this.parentType, this.parentId, c, networkId, targetId));
      });
      promise = zip.generateAsync({type: 'blob'}).then((blob: any) => {
        a.href = this.window.URL.createObjectURL(blob);
        a.download = fileBase + '.zip';
      });
    } else if (downloadFormat === 'ZIPPED_TXT') {
      const zip = new JSZip();

      _.forEach(creatives, (c) => {
        zip.file(_.kebabCase(c.name) + '.txt', this.generateCode(codeFormat, null, adTagCount, c));
        adTagCount++;
      });
      promise = zip.generateAsync({type: 'blob'}).then((blob: any) => {
        a.href = this.window.URL.createObjectURL(blob);
        a.download = fileBase + '.zip';
      });
    } else if (downloadFormat === 'TXT') {
      let outputString = '';
      _.forEach(creatives, (c) => {
        c.code = this.generateCode(codeFormat, null, adTagCount, c);
        outputString += c.code + '\n\n\n';
        adTagCount++;
      });

      const blob = new this.window.Blob([outputString], {type: 'text/plain'});
      a.href = this.window.URL.createObjectURL(blob);
      a.download = fileBase + '.txt';
      promise = Promise.resolve();
    } else if (downloadFormat === 'XLS') {
      if (codeFormat === 'GOOGLE_CAMPAIGN_MANAGER') {
        this.writeGoogleCampaignManagerXls(data, creatives, adTagCount, codeFormat);
        return;
      }

      if (codeFormat === 'ADFORM') {
        this.writeAdFormXls(data, creatives, adTagCount, codeFormat);
        return;
      }
    }

    if (!promise) {
      return;
    }
    promise.then(() => {
      this.document.body.appendChild(a);
      a.click();
      this.document.body.removeChild(a);
      this.window.URL.revokeObjectURL(a.href);
    });
  }

  private whereTheActionHappens(data: any): void {
    this.actionData = data;
    this.optionCode = {};
    _.forEach(this.showOptions, (opt) => {
      this.optionCode[opt] = {};
    });

    this.parentId = _.get(data, ['lineItemInfo', 'id'])
      || _.get(data, ['lineItem', 'id'])
      || _.get(data, ['creativeSet', 'id'])
      || _.get(data, ['model', 'id'])
      || this.resolveDirectParentId(data);
    this.lineItemName = _.get(data, ['lineItemInfo', 'name'])
      || _.get(data, ['lineItem', 'name'])
      || _.get(data, ['creativeSet', 'name'])
      || _.get(data, ['model', 'name'])
      || _.get(data, ['name']);
    this.creative = (this.type !== 'ORDER' && this.type !== 'LINE_ITEM' && this.type !== 'CREATIVE_SET'
      ? _.get(data, ['model'])
      : null);
    this.parentType = _.get(this.model, ['creativeSetParent']) || this.type === 'CREATIVE_SET'
      ? 'creativeSetId'
      : this.type === 'ORDER' ? 'orderId' : 'lineItemId';
    this.adTagId = '';

    this.dimensions = _.isArray(this.dimensions) ? this.dimensions : [[]];
    this.dimensions[0] = _.isArray(this.dimensions[0]) ? this.dimensions[0] : [];

    this.updateThirdPartyCode();
  }

  private generateCode(codeFormat: string, variation?: number, targetSuffix?: number | string, pCreative?: any): string {
    const theTargetId = targetSuffix ? this.targetId + '-' + targetSuffix : this.targetId;
    const auW = _.get(pCreative, ['width']) || this.auW;
    const auH = _.get(pCreative, ['height']) || this.auH;
    const codeCreative = pCreative || this.creative;
    if (codeFormat === 'STANDARD_JAVASCRIPT') {
      if (variation === 2) {
        return CreativeDefaults.getScriptServe(
          this.parentType, this.parentId, codeCreative, this.adTagId,
          this.LocalNetworkProfile.get('tagId'), this.useCookies, this.dimensions,
          this.clickTrackingUrl, this.targeting
        );
      } else if (variation === 3) {
        return CreativeDefaults.getThirdParty(
          this.parentType, this.parentId, codeCreative, this.adTagId,
          this.LocalNetworkProfile.get('tagId'), this.useCookies, this.dimensions,
          auW, auH, this.clickTrackingUrl, this.targeting
        );
      }
      return CreativeDefaults.getJs(
        this.parentType, this.parentId, codeCreative, this.adTagId,
        this.LocalNetworkProfile.get('tagId'), this.useCookies, theTargetId,
        this.dimensions, auW, auH, this.clickTrackingUrl, this.targeting
      );
    } else if (codeFormat === 'GOOGLE_AD_MANAGER') {
      if (variation === 2) {
        return CreativeDefaults.getScriptServeGAM(
          this.parentType, this.parentId, codeCreative, this.adTagId,
          this.LocalNetworkProfile.get('tagId'), this.useCookies, this.dimensions, this.targeting
        );
      }
      return CreativeDefaults.getJsGAM(
        this.parentType, this.parentId, codeCreative, this.adTagId,
        this.LocalNetworkProfile.get('tagId'), this.useCookies, theTargetId,
        this.dimensions, auW, auH, this.targeting
      );
    } else if (codeFormat === 'DV360') {
      return CreativeDefaults.getJsGAM(
        this.parentType, this.parentId, codeCreative, this.adTagId,
        this.LocalNetworkProfile.get('tagId'), this.useCookies, theTargetId,
        this.dimensions, auW, auH, this.targeting
      );
    } else if (codeFormat === 'XANDR') {
      return CreativeDefaults.getXandr(
        this.parentType, this.parentId, codeCreative, this.adTagId,
        this.LocalNetworkProfile.get('tagId'), this.useCookies, theTargetId,
        this.dimensions, auW, auH, this.targeting
      );
    } else if (codeFormat === 'ADFORM') {
      return CreativeDefaults.getAdForm(
        this.parentType, this.parentId, codeCreative, this.adTagId,
        this.LocalNetworkProfile.get('tagId'), this.useCookies, theTargetId,
        this.dimensions, auW, auH, this.targeting
      );
    } else if (codeFormat === 'GOOGLE_CAMPAIGN_MANAGER') {
      return CreativeDefaults.getGoogleCampaignManager(
        this.parentType, this.parentId, codeCreative, this.adTagId,
        this.LocalNetworkProfile.get('tagId'), this.useCookies, theTargetId,
        this.dimensions, auW, auH, this.targeting
      );
    }
    return '';
  }

  private writeGoogleCampaignManagerXls(data: any, creatives: any[], adTagCount: number, codeFormat: string): void {
    const defaultHeaderValue = {
      value: '', fontWeight: 'bold', align: 'center', color: '#ffffff',
      backgroundColor: '#0d5d9e', width: 30, height: 25, alignVertical: 'center'
    };
    const summaryHeading = [
      [_.assign({}, defaultHeaderValue, {value: 'Contract Information', span: 2})]
    ];
    const summaryData = [
      [{value: 'Advertiser ID'}, {value: data.advertiser.id}],
      [{value: 'Advertiser Name'}, {value: data.advertiser.name}],
      [{value: 'Campaign ID'}, {value: data.order.name}],
      [{value: 'Campaign Name'}, {value: data.order.id}]
    ];
    const blankRow = [{value: ''}];
    const adFormSubHeading = [[
      _.assign({}, defaultHeaderValue, {value: 'Advertiser ID'}),
      _.assign({}, defaultHeaderValue, {value: 'Advertiser Name'}),
      _.assign({}, defaultHeaderValue, {value: 'Campaign ID'}),
      _.assign({}, defaultHeaderValue, {value: 'Campaign Name'}),
      _.assign({}, defaultHeaderValue, {value: 'Placement ID'}),
      _.assign({}, defaultHeaderValue, {value: 'Placement External ID'}),
      _.assign({}, defaultHeaderValue, {value: 'Site'}),
      _.assign({}, defaultHeaderValue, {value: 'Placement Name'}),
      _.assign({}, defaultHeaderValue, {value: 'Placement Compatibility'}),
      _.assign({}, defaultHeaderValue, {value: 'Dimensions'}),
      _.assign({}, defaultHeaderValue, {value: 'Start Date'}),
      _.assign({}, defaultHeaderValue, {value: 'End Date'}),
      _.assign({}, defaultHeaderValue, {value: 'Standard Tag'}),
      _.assign({}, defaultHeaderValue, {value: 'Iframes/JavaScript Tag'}),
      _.assign({}, defaultHeaderValue, {value: 'Internal Redirect Tag'}),
      _.assign({}, defaultHeaderValue, {value: 'JavaScript Tag'})
    ]];

    const allData = [];
    let count = adTagCount;
    _.forEach(creatives, (c) => {
      const code = this.generateCode(codeFormat, null, count, c);
      count++;
      allData.push([
        {value: data.advertiser.id, alignVertical: 'top'},
        {value: data.advertiser.name, alignVertical: 'top'},
        {value: data.order.id, alignVertical: 'top'},
        {value: data.order.name, alignVertical: 'top'},
        {value: data.lineItem.id, alignVertical: 'top'},
        {value: '', alignVertical: 'top'},
        {value: '', alignVertical: 'top'},
        {value: data.lineItem.name, alignVertical: 'top'},
        {value: 'Display', alignVertical: 'top'},
        {value: c.width + 'x' + c.height, alignVertical: 'top'},
        {value: formatNetworkDateTime(data.lineItem.startDate, this.LocalNetworkProfile.get('timeZone') || 'utc', 'YYYY-MM-DD HH:mm:ss'), alignVertical: 'top'},
        {value: formatNetworkDateTime(data.lineItem.endDate, this.LocalNetworkProfile.get('timeZone') || 'utc', 'YYYY-MM-DD HH:mm:ss'), alignVertical: 'top'},
        {value: '', alignVertical: 'top'},
        {value: code, alignVertical: 'top'},
        {value: '', alignVertical: 'top'},
        {value: code, alignVertical: 'top'}
      ]);
    });
    writeXlsxFile([...summaryHeading, ...summaryData, blankRow, ...adFormSubHeading, ...allData], {
      fileName: 'fred.xls',
      columns: [
        {width: 30}, {width: 30}, {width: 30}, {width: 30}, {width: 30}, {width: 5},
        {width: 30}, {width: 30}, {width: 30}, {width: 30}, {width: 30}, {width: 30},
        {width: 30}, {width: 30}, {width: 30}, {width: 30}
      ]
    });
  }

  private writeAdFormXls(data: any, creatives: any[], adTagCount: number, codeFormat: string): void {
    const defaultHeaderValue = {
      value: '', fontWeight: 'bold', align: 'center', color: '#ffffff',
      backgroundColor: '#0d5d9e', width: 30, height: 25, alignVertical: 'center'
    };
    const summaryHeading = [
      [_.assign({}, defaultHeaderValue, {value: 'Summary', span: 2})]
    ];
    const summaryData = [
      [{value: 'Export date'}, {value: moment().format('YYYY-MM-DD')}],
      [{value: 'Advertiser name'}, {value: data.advertiser.name}],
      [{value: 'Campaign name'}, {value: data.order.name}],
      [{value: 'Campaign ID'}, {value: data.order.id}]
    ];
    const blankRow = [{value: ''}];
    const adFormHeading = [[
      _.assign({}, defaultHeaderValue, {value: 'Line item details', span: 5}),
      null, null, null, null,
      {value: ''},
      _.assign({}, defaultHeaderValue, {value: 'Tag details', span: 17}),
      null, null, null, null, null, null, null, null, null, null, null, null, null, null
    ]];
    const adFormSubHeading = [[
      _.assign({}, defaultHeaderValue, {value: 'Media'}),
      _.assign({}, defaultHeaderValue, {value: 'Line item name'}),
      _.assign({}, defaultHeaderValue, {value: 'Line item ID'}),
      _.assign({}, defaultHeaderValue, {value: 'Start date'}),
      _.assign({}, defaultHeaderValue, {value: 'End date'}),
      {value: ''},
      _.assign({}, defaultHeaderValue, {value: 'Type'}),
      _.assign({}, defaultHeaderValue, {value: 'Tag ID'}),
      _.assign({}, defaultHeaderValue, {value: 'Tag name'}),
      _.assign({}, defaultHeaderValue, {value: 'Ad type'}),
      _.assign({}, defaultHeaderValue, {value: 'Dimensions'}),
      _.assign({}, defaultHeaderValue, {value: 'Script'}),
      _.assign({}, defaultHeaderValue, {value: 'Additional information'}),
      _.assign({}, defaultHeaderValue, {value: 'Target URL (click tracker)'}),
      _.assign({}, defaultHeaderValue, {value: '1x1 pixel counter (impression tracker)'}),
      _.assign({}, defaultHeaderValue, {value: 'Format'}),
      _.assign({}, defaultHeaderValue, {value: 'Destination URL'}),
      _.assign({}, defaultHeaderValue, {value: 'Append URL'}),
      _.assign({}, defaultHeaderValue, {value: 'Replace URL'}),
      _.assign({}, defaultHeaderValue, {value: 'Impression tracking pixel'}),
      _.assign({}, defaultHeaderValue, {value: 'Click tracking pixel'}),
      _.assign({}, defaultHeaderValue, {value: 'Ad name'}),
      _.assign({}, defaultHeaderValue, {value: 'Preview'})
    ]];

    const allData = [];
    let count = adTagCount;
    _.forEach(creatives, (c) => {
      const code = this.generateCode(codeFormat, null, count, c);
      const targetId = this.targetId + '-' + count;
      const previewUrl = this.$state.href('nude.preview', {
        creativeId: c.id,
        networkId: this.LocalNetworkProfile.getNetworkTag(),
        creativeWidth: c.width,
        creativeHeight: c.height,
        lineItemId: c.lineItemId,
        lineItemCode: c.lineItemCode
      }, {absolute: true});
      count++;

      allData.push([
        {value: data.advertiser.advertiserUrl, alignVertical: 'top'},
        {value: data.lineItem.name, alignVertical: 'top'},
        {value: data.lineItem.id, alignVertical: 'top'},
        {value: formatNetworkDateTime(data.lineItem.startDate, this.LocalNetworkProfile.get('timeZone') || 'utc', 'YYYY-MM-DD HH:mm:ss'), alignVertical: 'top'},
        {value: formatNetworkDateTime(data.lineItem.endDate, this.LocalNetworkProfile.get('timeZone') || 'utc', 'YYYY-MM-DD HH:mm:ss'), alignVertical: 'top'},
        {value: '', alignVertical: 'top'},
        {value: 'Direct', alignVertical: 'top'},
        {value: targetId, alignVertical: 'top'},
        {value: _.kebabCase(c.name + ' ' + c.width) + 'x' + c.height, alignVertical: 'top'},
        {value: _.startCase(_.lowerCase(_.get(c, ['layout', 'detectedCategory']))), alignVertical: 'top'},
        {value: c.width + 'x' + c.height, alignVertical: 'top'},
        {value: code, alignVertical: 'top'},
        {value: '', alignVertical: 'top'},
        {
          value: CreativeDefaults.getClickTracker(
            this.parentType, this.parentId, this.creative, this.adTagId,
            this.LocalNetworkProfile.get('tagId'), this.useCookies
          ),
          alignVertical: 'top'
        },
        {
          value: CreativeDefaults.getImpTracker(
            this.parentType, this.parentId, this.creative, this.adTagId,
            this.LocalNetworkProfile.get('tagId'), this.useCookies
          ),
          alignVertical: 'top'
        },
        {value: 'javascriptDefault', alignVertical: 'top'},
        {value: c.url, alignVertical: 'top'},
        {value: '', alignVertical: 'top'},
        {value: '', alignVertical: 'top'},
        {value: '', alignVertical: 'top'},
        {value: '', alignVertical: 'top'},
        {value: c.name, alignVertical: 'top'},
        {value: previewUrl, alignVertical: 'top'}
      ]);
    });
    writeXlsxFile([...summaryHeading, ...summaryData, blankRow, ...adFormHeading, ...adFormSubHeading, ...allData], {
      fileName: _.kebabCase(data.lineItem.name) + '.xls',
      columns: [
        {width: 30}, {width: 30}, {width: 30}, {width: 30}, {width: 30}, {width: 5},
        {width: 30}, {width: 30}, {width: 30}, {width: 30}, {width: 30}, {width: 30},
        {width: 30}, {width: 30}, {width: 30}, {width: 30}, {width: 30}, {width: 30},
        {width: 30}, {width: 30}, {width: 30}, {width: 30}, {width: 30}
      ]
    });
  }
}
